<template>
  <div class="page-shell">
    <section class="container">
      <div class="page-intro">
        <p class="eyebrow">Study History</p>
        <h1 class="page-title">我的学习历史</h1>
        <p class="page-description">
          查看您最近学习过的课程，继续您的学习之旅
        </p>
      </div>

      <div v-if="loading" class="status-card">正在加载学习历史...</div>

      <div v-else-if="historyList.length === 0" class="status-card">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M2 12h20" />
        </svg>
        <p>暂无学习历史</p>
        <router-link :to="{ name: 'courses' }" class="btn btn--primary">
          去课程中心看看
        </router-link>
      </div>

      <div v-else class="history-container">
        <div class="history-actions">
          <button
            v-if="selectedIds.length > 0"
            class="btn btn--secondary"
            @click="handleBatchDelete"
            :disabled="deleting"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            删除选中 ({{ selectedIds.length }})
          </button>
          <span class="history-count">共 {{ pagination.total }} 条记录</span>
        </div>

        <div class="history-list">
          <article
            v-for="item in historyList"
            :key="item.history_id"
            class="history-card"
            :class="{ 'is-selected': selectedIds.includes(item.history_id) }"
          >
            <div class="history-card__checkbox">
              <input
                type="checkbox"
                :id="`history-${item.history_id}`"
                :value="item.history_id"
                v-model="selectedIds"
              />
              <label :for="`history-${item.history_id}`"></label>
            </div>

            <router-link
              :to="{ name: 'course-detail', params: { id: item.course_id } }"
              class="history-card__cover"
            >
              <img :src="item.cover" :alt="item.title" />
              <div class="history-card__overlay">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
            </router-link>

            <div class="history-card__content">
              <div class="history-card__header">
                <router-link
                  :to="{ name: 'course-detail', params: { id: item.course_id } }"
                  class="history-card__title"
                >
                  {{ item.title }}
                </router-link>
                <div class="history-card__badges">
                  <span class="pill" :class="item.status === 1 ? 'pill--success' : 'pill--muted'">
                    {{ item.status_word }}
                  </span>
                  <span class="pill pill--info">{{ item.serialize_status_word }}</span>
                </div>
              </div>

              <p class="history-card__description">{{ formatHistoryDescription(item.description) }}</p>

              <div class="history-card__meta">
                <div class="history-card__info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                  <span>{{ item.lecturer_name }}</span>
                </div>
                <div class="history-card__info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2v20M2 12h20" />
                  </svg>
                  <span>{{ item.category_one_name }} / {{ item.category_two_name }}</span>
                </div>
                <div class="history-card__info history-card__time">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  <span>最后学习：{{ item.last_study_at }}</span>
                </div>
              </div>
            </div>

            <div class="history-card__actions">
              <button
                class="btn btn--ghost btn--sm"
                @click="handleDelete(item.history_id)"
                :disabled="deleting"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                删除
              </button>
            </div>
          </article>
        </div>

        <div v-if="pagination.last_page > 1" class="pagination">
          <button
            class="btn btn--secondary"
            :disabled="pagination.current_page === 1"
            @click="loadHistory(pagination.current_page - 1)"
          >
            上一页
          </button>
          <span class="pagination__info">
            第 {{ pagination.current_page }} / {{ pagination.last_page }} 页
          </span>
          <button
            class="btn btn--secondary"
            :disabled="pagination.current_page === pagination.last_page"
            @click="loadHistory(pagination.current_page + 1)"
          >
            下一页
          </button>
        </div>
      </div>
    </section>

    <!-- Token 失效弹窗 -->
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { fetchCourseHistory, removeCourseHistory } from '@/api/course'
import AuthModal from '@/components/AuthModal.vue'
import authErrorHandler from '@/mixins/authErrorHandler'

export default {
  name: 'StudyHistoryView',
  components: {
    AuthModal
  },
  mixins: [authErrorHandler],
  data() {
    return {
      loading: true,
      deleting: false,
      historyList: [],
      selectedIds: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadHistory()
  },
  methods: {
    async loadHistory(page = 1) {
      this.loading = true
      this.selectedIds = []

      try {
        const response = await fetchCourseHistory({
          page,
          pageSize: 10
        })

        this.historyList = response.data
        this.pagination = {
          current_page: response.current_page,
          last_page: response.last_page,
          per_page: response.per_page,
          total: response.total
        }
      } catch (error) {
        // 检查是否是认证错误
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          alert(error.message || '学习历史加载失败')
        }
      } finally {
        this.loading = false
      }
    },
    decodeHtmlEntities(value) {
      if (typeof document === 'undefined') {
        return value
      }

      const textarea = document.createElement('textarea')
      textarea.innerHTML = value
      return textarea.value
    },
    formatHistoryDescription(value = '') {
      const rawValue = String(value || '').trim()

      if (!rawValue) {
        return '暂无简介'
      }

      let normalized = rawValue

      for (let index = 0; index < 2; index += 1) {
        const decoded = this.decodeHtmlEntities(normalized)

        if (decoded === normalized) {
          break
        }

        normalized = decoded
      }

      if (typeof DOMParser === 'undefined') {
        return normalized
          .replace(/<img\b[^>]*>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim() || '暂无简介'
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(normalized, 'text/html')

      doc.querySelectorAll('img, picture, video, audio, iframe, svg').forEach(node => {
        node.remove()
      })

      return (doc.body.textContent || '')
        .replace(/\s+/g, ' ')
        .trim() || '暂无简介'
    },
    async handleDelete(id) {
      if (!confirm('确定要删除这条学习历史吗？')) {
        return
      }

      this.deleting = true

      try {
        await removeCourseHistory(id)
        await this.loadHistory(this.pagination.current_page)
      } catch (error) {
        if (this.isAuthError(error)) {
          this.showAuthModal = true
        } else {
          alert(error.message || '删除失败')
        }
      } finally {
        this.deleting = false
      }
    },
    async handleBatchDelete() {
      if (this.selectedIds.length === 0) {
        return
      }

      if (!confirm(`确定要删除选中的 ${this.selectedIds.length} 条学习历史吗？`)) {
        return
      }

      this.deleting = true

      try {
        await removeCourseHistory(this.selectedIds)
        await this.loadHistory(this.pagination.current_page)
      } catch (error) {
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          alert(error.message || '删除失败')
        }
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.history-container {
  display: grid;
  gap: 24px;
}

.history-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba($color-surface, 0.72);
  border: 1px solid rgba($color-border, 0.94);
}

.history-count {
  color: $color-text-secondary;
  font-size: 14px;
}

.history-list {
  display: grid;
  gap: 16px;
}

.history-card {
  display: grid;
  grid-template-columns: auto 200px 1fr auto;
  gap: 20px;
  padding: 20px;
  border-radius: 20px;
  background: $color-surface;
  border: 2px solid rgba($color-border, 0.94);
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba($color-primary, 0.3);
    box-shadow: $shadow-md;
  }

  &.is-selected {
    border-color: $color-primary;
    background: rgba($color-primary-light, 0.5);
  }
}

.history-card__checkbox {
  display: flex;
  align-items: center;

  input[type="checkbox"] {
    display: none;

    &:checked + label::after {
      opacity: 1;
      transform: scale(1);
    }
  }

  label {
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    border: 2px solid $color-border;
    border-radius: 6px;
    background: $color-surface;
    cursor: pointer;
    transition: all 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 6px;
      width: 4px;
      height: 8px;
      border: solid $color-surface;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg) scale(0);
      opacity: 0;
      transition: all 0.2s ease;
    }

    &:hover {
      border-color: $color-primary;
    }
  }

  input[type="checkbox"]:checked + label {
    background: $color-primary;
    border-color: $color-primary;
  }
}

.history-card__cover {
  position: relative;
  width: 200px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.history-card__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($color-text-primary, 0.4);
  opacity: 0;
  transition: opacity 0.2s ease;

  svg {
    color: $color-surface;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
}

.history-card__cover:hover .history-card__overlay {
  opacity: 1;
}

.history-card__content {
  display: grid;
  gap: 12px;
  align-content: start;
}

.history-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.history-card__title {
  font-size: 18px;
  font-weight: 600;
  color: $color-text-primary;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: $color-primary;
  }
}

.history-card__badges {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.history-card__description {
  color: $color-text-secondary;
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.history-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
}

.history-card__info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: $color-text-secondary;
  font-size: 13px;

  svg {
    flex-shrink: 0;
  }
}

.history-card__time {
  color: $color-primary;
  font-weight: 500;
}

.history-card__actions {
  display: flex;
  align-items: center;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}

.pagination__info {
  color: $color-text-secondary;
  font-size: 14px;
}

@media (max-width: 960px) {
  .history-card {
    grid-template-columns: auto 160px 1fr;
    grid-template-rows: auto auto;
  }

  .history-card__cover {
    width: 160px;
    height: 96px;
  }

  .history-card__actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}

@media (max-width: 640px) {
  .history-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .history-card {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto;
    gap: 12px;
    padding: 16px;
  }

  .history-card__checkbox {
    grid-row: 1;
  }

  .history-card__cover {
    grid-column: 1 / -1;
    grid-row: 2;
    width: 100%;
    height: 180px;
  }

  .history-card__content {
    grid-column: 1 / -1;
    grid-row: 3;
  }

  .history-card__actions {
    grid-column: 1 / -1;
    grid-row: 4;
  }

  .history-card__meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
